import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import './styles/index.css'; // Import all styles via index.css
import { AudioPlayer } from './services/AudioPlayer';
import { SpotifyService } from './services/SpotifyService';
import { audioPlayerKey, spotifyServiceKey } from './injection-keys';
import { logger } from '@maso-dj/shared-types';

const app = createApp(App);
const pinia = createPinia();

// Create service instances
const audioPlayer = new AudioPlayer();
const spotifyService = new SpotifyService();

// Provide services to entire app
app.provide(audioPlayerKey, audioPlayer);
app.provide(spotifyServiceKey, spotifyService);

// Check if this is a local-only build (container mode)
// LOCAL-ONLY MODE CONFIGURATION:
// - Frontend: VITE_LOCAL_ONLY_BUILD (build-time flag) - Excludes Auth0 from bundle entirely
// - Backend: LOCAL_ONLY_MODE (runtime flag) - Uses local JWT auth, local storage, disables Stripe
// These work together for offline/self-hosted deployments without cloud dependencies
const isLocalOnlyBuild = import.meta.env.VITE_LOCAL_ONLY_BUILD === 'true';

if (!isLocalOnlyBuild) {
  // Regular build with Auth0 support
  const { createAuth0 } = await import('@auth0/auth0-vue');

  app.use(
    createAuth0({
      domain: import.meta.env.VITE_AUTH0_DOMAIN || 'githubfolders.eu.auth0.com',
      clientId: import.meta.env.VITE_AUTH0_CLIENT_ID || '',
      authorizationParams: {
        redirect_uri: `${window.location.origin}/callback/auth`,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE || 'https://githubfolders.eu.auth0.com/api/v2/',
        scope: 'openid profile email', // Explicitly request email claim in tokens
      },
      cacheLocation: 'localstorage', // Persist auth state across redirects (e.g., Stripe checkout)
      useRefreshTokens: true, // Enable refresh tokens for better session persistence
    })
  );
} else {
  logger.info('Local-only build - Auth0 disabled');
}

app.use(pinia);
app.use(router);
app.mount('#app');
